// ABOUTME: W4 admin analysis panel — per-hypothesis readiness table + queue deep-analysis runs
// ABOUTME: One deep-analysis job per hypothesis; status/badges driven by the shared jobs feed

import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Play } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ActivityPanel } from '@/components/interviews/ActivityPanel'
import { useQueryClient } from '@tanstack/react-query'
import { Textarea } from '@/components/ui/textarea'
import { hypothesisEditApi } from '@/api/analysis'
import { useReadiness, useRunAnalysis } from '@/hooks/useAnalysis'
import { activeJobFor, useJobs } from '@/hooks/useInterviews'
import { hypothesisCode, hypothesisPath } from '@/lib/hypothesisIds'
import { HypothesisReadiness } from '@/api/analysis'

const MINUTES_PER_ANALYSIS = 3 // deep-analysis model run, order of magnitude

function errText(err: any): string {
  const d = err?.response?.data?.detail
  return typeof d === 'string' ? d : (err?.message || 'Request failed')
}

export function AnalysisPage() {
  const { data, isLoading, isError, refetch } = useReadiness()
  const { data: jobs } = useJobs()
  const runAnalysis = useRunAnalysis()
  const [confirmIds, setConfirmIds] = useState<string[] | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [liveMessage, setLiveMessage] = useState('')
  const [newOpen, setNewOpen] = useState(false)
  const [newStatement, setNewStatement] = useState('')
  const [creating, setCreating] = useState(false)
  const queryClient = useQueryClient()

  const createHypothesis = async () => {
    setCreating(true)
    setActionError(null)
    try {
      const res = await hypothesisEditApi.createHypothesis(newStatement.trim())
      setLiveMessage(`Created ${res.hypothesis_id}`)
      setNewOpen(false)
      setNewStatement('')
      queryClient.invalidateQueries({ queryKey: ['hypothesis-readiness'] })
      queryClient.invalidateQueries({ queryKey: ['hypotheses'] })
      queryClient.invalidateQueries({ queryKey: ['interviews'] })
    } catch (e: any) {
      setActionError(e?.response?.data?.detail ?? 'Create failed')
    } finally {
      setCreating(false)
    }
  }

  const staleOrNever = useMemo(
    () => (data ?? []).filter((r) => r.status === 'stale' || r.status === 'never').map((r) => r.hypothesis_id),
    [data],
  )
  const runningAnalyses = (jobs ?? []).filter(
    (j) => j.kind === 'analysis' && (j.status === 'queued' || j.status === 'running'),
  ).length

  const run = (ids: string[]) => {
    setActionError(null)
    runAnalysis.mutate(ids, {
      onSuccess: (res) => {
        setLiveMessage(`Queued ${res.job_ids.length} analysis run${res.job_ids.length === 1 ? '' : 's'}`)
        setConfirmIds(null)
      },
      onError: (err) => {
        setActionError(errText(err))
        setConfirmIds(null)
      },
    })
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div aria-live="polite" className="sr-only">{liveMessage}</div>

      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-bold text-alx-blue">Hypothesis Analysis</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setNewOpen(true)}>New hypothesis</Button>
          {staleOrNever.length > 0 && (
            <Button onClick={() => setConfirmIds(staleOrNever)} disabled={runningAnalyses > 0}>
              Run all stale ({staleOrNever.length})
            </Button>
          )}
        </div>
      </div>
      <p className="text-sm text-gray-600 mb-6">
        Deep analysis runs read every piece of evidence for a hypothesis and produce a verdict.
        Runs queue one at a time{runningAnalyses > 0 && <> — <span className="font-medium">{runningAnalyses} in the queue now</span></>}.
      </p>

      {actionError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 flex items-center justify-between">
          <p className="text-sm text-red-700">{actionError}</p>
          <button className="text-sm text-red-700 underline" onClick={() => setActionError(null)}>Dismiss</button>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      ) : isError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex items-center justify-between">
          <p className="text-sm text-red-700">Couldn't load readiness.</p>
          <Button variant="outline" onClick={() => refetch()}>Retry</Button>
        </div>
      ) : (data ?? []).length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-10 text-center">
          <p className="text-sm text-gray-600">No hypotheses loaded — load the FIG framework first.</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <th className="px-4 py-3">Hypothesis</th>
                  <th className="px-4 py-3 text-right">Evidence</th>
                  <th className="px-4 py-3">Last analysis</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {(data ?? []).map((row) => (
                  <ReadinessRow
                    key={row.hypothesis_id}
                    row={row}
                    processing={!!activeJobFor(jobs, row.hypothesis_id)}
                    onRun={() => run([row.hypothesis_id])}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <ActivityPanel jobs={jobs} />
        </div>
      )}

      {/* New hypothesis */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New hypothesis</DialogTitle>
            <DialogDescription>
              Added to the FIG CoE study with the next free id. Existing interviews go stale
              until re-analyzed against it; signals can be added from the hypothesis page.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={newStatement}
            onChange={(e) => setNewStatement(e.target.value)}
            rows={3}
            placeholder="Hypothesis statement…"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>Cancel</Button>
            <Button onClick={createHypothesis} disabled={creating || !newStatement.trim()}>
              {creating ? 'Creating…' : 'Create hypothesis'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmIds} onOpenChange={(open) => !open && setConfirmIds(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Run {confirmIds?.length} deep analysis run{(confirmIds?.length ?? 0) === 1 ? '' : 's'}?</DialogTitle>
            <DialogDescription>
              Each reads every piece of evidence for its hypothesis and runs the deep-analysis model
              — roughly {MINUTES_PER_ANALYSIS} min each ≈ {Math.max(1, Math.round((confirmIds?.length ?? 0) * MINUTES_PER_ANALYSIS))} min
              total, one at a time. Ingestions queue behind them while they run.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmIds(null)}>Cancel</Button>
            <Button onClick={() => confirmIds && run(confirmIds)} disabled={runAnalysis.isPending}>
              {runAnalysis.isPending ? 'Queueing…' : 'Run analyses'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function ReadinessBadge({ status }: { status: HypothesisReadiness['status'] }) {
  const map = {
    'fresh': ['bg-alx-green/15 text-alx-blue', '● Fresh'],
    'stale': ['bg-alx-gold/20 text-alx-blue', '◐ Stale'],
    'never': ['bg-blue-100 text-blue-700', '○ Ready to run'],
    'no-evidence': ['bg-gray-200 text-gray-600', '— No evidence'],
  } as const
  const [cls, label] = map[status]
  return <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>{label}</span>
}

function ReadinessRow({ row, processing, onRun }: {
  row: HypothesisReadiness; processing: boolean; onRun: () => void
}) {
  const newEvidence =
    row.status === 'stale' && row.evidence_points_analyzed != null && row.evidence_points_analyzed > 0
      ? row.evidence_count - row.evidence_points_analyzed
      : null
  return (
    <tr className="border-b border-gray-100 last:border-0 hover:bg-alx-green/5 transition-colors">
      <td className="px-4 py-3 max-w-xl">
        <Link to={hypothesisPath(row.hypothesis_id)} className="font-medium text-alx-blue hover:underline">
          {hypothesisCode(row.hypothesis_id)}
        </Link>
        <span className="block text-xs text-gray-600 truncate" title={row.statement}>{row.statement}</span>
      </td>
      <td className="px-4 py-3 text-right text-gray-700">
        {row.evidence_count}
        {newEvidence != null && newEvidence > 0 && (
          <span className="ml-1 text-xs font-semibold text-alx-blue bg-alx-gold/20 px-1 rounded">+{newEvidence} new</span>
        )}
      </td>
      <td className="px-4 py-3 text-gray-700">
        {row.last_analysis_at ? (
          <>
            {row.last_analysis_at.slice(0, 10)}
            {row.last_verdict && (
              <span className="block text-xs text-gray-500">
                {row.last_verdict}{row.last_confidence && ` · ${row.last_confidence} confidence`}
              </span>
            )}
          </>
        ) : '—'}
      </td>
      <td className="px-4 py-3">
        {processing ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse motion-reduce:animate-none" />
            Processing
          </span>
        ) : (
          <ReadinessBadge status={row.status} />
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  size="icon" variant="ghost"
                  aria-label={`Run analysis for ${row.hypothesis_id}`}
                  disabled={processing || row.status === 'no-evidence'}
                  onClick={onRun}
                >
                  <Play className="w-4 h-4" />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {processing ? 'Analysis already queued/running'
                : row.status === 'no-evidence' ? 'No evidence to analyze yet'
                : 'Run deep analysis now'}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </td>
    </tr>
  )
}
